import { useState, useEffect, Suspense } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Layout, Typography, theme, Button, Spin } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Bubble, Sender } from '@ant-design/x'

const { Header, Content } = Layout
const { Title } = Typography

interface ChatMessage {
  key: string
  content: string
  role?: 'user' | 'assistant'
}

function ChatContent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { token } = theme.useToken()
  const [initialInstruction, setInitialInstruction] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const instruction = searchParams.get('instruction') || ''
    const files = searchParams.get('files') || ''

    setInitialInstruction(instruction)
    if (files) {
      setUploadedFiles(files.split(','))
    }

    if (instruction) {
      handleSendMessage(instruction)
    }
  }, [searchParams])

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return

    const userMessage: ChatMessage = { key: Date.now().toString(), content, role: 'user' }
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    await new Promise(resolve => setTimeout(resolve, 1000))

    let response = ''
    if (uploadedFiles.length > 0 && messages.length === 0) {
      response = `I've received your files: ${uploadedFiles.join(', ')}. `
    }
    response += `I understand you want to: "${content}". I'm here to help you process this accounting document. How would you like to proceed?`

    const aiMessage: ChatMessage = { key: (Date.now() + 1).toString(), content: response, role: 'assistant' }
    setMessages(prev => [...prev, aiMessage])
    setIsLoading(false)
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: token.colorBgLayout }}>
      <Header style={{ backgroundColor: token.colorBgContainer, borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Back to Dashboard</Button>
        <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>AI Document Assistant</Title>
      </Header>

      <Content style={{ padding: 24, display: 'flex', flexDirection: 'column', maxWidth: 1000, margin: '0 auto', width: '100%', gap: 16 }}>
        {uploadedFiles.length > 0 && (
          <div style={{ backgroundColor: token.colorBgContainer, padding: 16, borderRadius: 12, border: `1px solid ${token.colorBorderSecondary}` }}>
            <Typography.Text strong>Uploaded Files:</Typography.Text>
            <div style={{ marginTop: 8 }}>
              {uploadedFiles.map((file, index) => (
                <div key={index} style={{ padding: '4px 12px', backgroundColor: token.colorBgTextHover, borderRadius: 8, display: 'inline-block', marginRight: 8, marginBottom: 8 }}>📄 {file}</div>
              ))}
            </div>
          </div>
        )}

        <div style={{ flex: 1, backgroundColor: token.colorBgContainer, borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', border: `1px solid ${token.colorBorderSecondary}`, minHeight: 'calc(100vh - 200px)' }}>
          <div style={{ flex: 1, overflow: 'auto', marginBottom: 16 }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: token.colorTextSecondary }}>
                <Typography.Title level={5} style={{ color: token.colorTextSecondary }}>Start a conversation</Typography.Title>
                <Typography.Text>Ask me anything about your accounting documents</Typography.Text>
              </div>
            ) : (
              <Bubble.List items={messages} />
            )}
          </div>

          <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 16 }}>
            <Sender onSubmit={handleSendMessage} loading={isLoading} placeholder="Ask me anything about your documents..." style={{ backgroundColor: token.colorBgContainer }} />
          </div>
        </div>
      </Content>
    </Layout>
  )
}

export default function ChatPage() {
  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><Spin size="large" /></div>}>
      <ChatContent />
    </Suspense>
  )
}
